// Import necessary DataTables modules from npm
import DataTable from 'datatables.net-dt';
import { ls } from './config.js';

// Define the TinCanReportTable object
export const TinCanReportTable = {
	apiUrl: '',
	nonce: '',
	endpoint: '',
	tableElement: null,

	// Initialize the table on page load
	init: function (tableElement) {
		this.tableElement = tableElement;
		this.apiUrl = reportingApiSetup.root; // REST API base URL
		this.nonce = reportingApiSetup.nonce; // Nonce for security
		this.endpoint = 'tin_can_report_data'; // REST API endpoint

		// Build the table with pipeline functionality
		this.buildTable();
	},

	// Build the DataTable with pipeline
	// Build the DataTable with additional options and buttons
	buildTable: function () {
		const self = this;
		var fileName,
			now,
			date;

		now = new Date(Date.now());
		date = now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate();

		fileName = 'tin_can_report_' + date;

		// Define the column headings and other table settings
		const headings = TincannyData.tinCanReportColumns;

		const columnDefs = []; // Define your custom column definitions as needed

		// DataTable initialization with pipeline and extra options
		new DataTable(self.tableElement, {
			ajax: self.pipeline({
				url: `${this.apiUrl}${this.endpoint}`, // Full REST API URL for the endpoint
				pages: 10, // Number of pages to cache (10 pages at a time)
				data: function (request) {
					// Append nonce and any other data needed for the API
					request._wpnonce = self.nonce;
					// Replace column index with `data` for sorting
					if (request.order && request.order.length > 0) {
						request.order.forEach(function (orderItem) {
							const columnIndex = orderItem.column;
							orderItem.column = request.columns[columnIndex].data; // Get the `data` value
						});
					}
					// Append the full query string to the request
					const queryString = window.location.search;
					if (queryString) {
						// Parse query string and add parameters to the request object
						const urlParams = new URLSearchParams(queryString);
						urlParams.forEach((value, key) => {
							request[key] = value;
						});
					}
					return request;
				},
				method: 'POST' // Use POST method for REST API
			}),
			// Show overlay when processing
			initComplete: function () {
				const overlay = document.getElementById("tableOverlay");
				this.on('processing.dt', function (e, settings, processing) {
					overlay.style.display = processing ? 'flex' : 'none';
				});
			},
			columns: headings,
			order: [[8, 'desc']],
			ordering: true,
			columnDefs: columnDefs,
			lengthMenu: [[10, 25, 50, 100, 200, 500, 1000], [10, 25, 50, 100, 200, 500, 1000]],
			pageLength: parseInt(TincannyData.pageLength, 10),
			responsive: true,
			keys: true,
			fixedHeader: {
				header: true,
				headerOffset: 32,
				footer: false
			},
			language: {
				sEmptyTable: ls("There are no activities to report."),
				sInfo: ls("Showing _START_ to _END_ of _TOTAL_ entries"),
				sInfoEmpty: ls("Showing 0 to 0 of 0 entries"),
				sInfoFiltered: ls(""),
				sLengthMenu: ls("Show _MENU_ entries"),
				sLoadingRecords: ls("Loading..."),
				sProcessing: ls("Processing..."),
				sSearch: '',
				searchPlaceholder: reportingApiSetup.localizedStrings.tablesSearchPlaceholder,
				sZeroRecords: ls("No matching records found"),
				oPaginate: {
					sFirst: ls("First"),
					sLast: ls("Last"),
					sNext: ls("Next"),
					sPrevious: ls("Previous")
				},
				oAria: {
					sSortAscending: ls(": activate to sort column ascending"),
					sSortDescending: ls(": activate to sort column descending")
				}
			},
			dom: '<"reporting-datatable"<"reporting-datatable__top"<"reporting-datatable__search"l><"reporting-datatable-top__middle"><"reporting-datatable__buttons"B>><"reporting-datatable__table"t><"reporting-datatable__bottom"<"reporting-datatable__bottom-left"<"reporting-datatable__bottom-left-info"i>><"reporting-datatable__bottom-right"p>>>',
			buttons: [
				{
					extend: 'csv',
					text: ls('CSV Export'),
					title: fileName,
					charset: 'utf-8',
					bom: true,
					action: function action(e, dt, button, config, cb) {
						const dataTableInstance = this;

						// Build the request payload
						const request = {
							length: -1, // Fetch all records
							is_csv: 1
						};

						// Append nonce and other data needed for the API
						request._wpnonce = self.nonce;

						// Append the full query string to the request
						const queryString = window.location.search;
						if (queryString) {
							// Parse query string and add parameters to the request object
							const urlParams = new URLSearchParams(queryString);
							urlParams.forEach((value, key) => {
								request[key] = value;
							});
						}
						// Fetch complete data from server (ignore pagination)
						fetch(`${self.apiUrl}${self.endpoint}`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								'X-WP-Nonce': self.nonce
							},
							body: JSON.stringify(request)
						})
							.then(response => response.json())
							.then(fullData => {
								// Use the full dataset for export
								const exportData = fullData.data;

								// Fetch column titles (header row) from DataTables
								const exportColumns = dt.settings().init().columns.map(col => col.data);
								const headers = dt.settings().init().columns.map(col => col.title); // Get column titles

								// Create CSV rows
								const headerRow = headers.join(','); // Combine headers into a single row
								const exportRows = exportData.map(row => exportColumns.map(col => `"${row[col]}"`).join(',')).join('\n');

								// Combine header and data rows
								const csvContent = `${headerRow}\n${exportRows}`;

								// Create a Blob object with CSV data
								const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

								// Use native JavaScript to trigger the download
								const link = document.createElement('a');
								link.href = URL.createObjectURL(csvBlob);
								link.download = fileName + '.csv';
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);

								// Stop the loading indicator
								cb();
							})
							.catch(error => {
								console.error('Error fetching full dataset for export:', error);
								cb(); // Ensure the loading stops even in case of an error
							});
					}
				}
			],
			processing: true,
			serverSide: true, // Enable server-side processing
		});
	},

	// DataTables pipeline for caching without jQuery, using POST method
	pipeline: function (opts) {
		let conf = {
			pages: opts.pages || 10, // Number of pages to cache
			url: opts.url || '', // URL of the API
			data: opts.data || null, // Additional data to send with the request
			method: opts.method || 'POST' // Use POST method for REST API
		};

		let cacheLower = -1;
		let cacheUpper = null;
		let cacheLastRequest = null;
		let cacheLastJson = null;

		return async function (request, drawCallback, settings) {
			// Determine if an AJAX call is necessary
			let ajax = false;
			// Parse `start` and `length` as integers to prevent concatenation issues
			let requestStart = parseInt(request.start, 10);
			let drawStart = requestStart;
			let requestLength = parseInt(request.length, 10);
			let requestEnd = requestStart + requestLength;

			// Calculate rows per fetch based on page size
			const rowsPerFetch = requestLength * conf.pages;

			if (settings.clearCache) {
				ajax = true;
				settings.clearCache = false;
			} else if (cacheLower < 0 || requestStart < cacheLower || requestEnd > cacheUpper) {
				// Out of cache range, fetch new data
				ajax = true;
			} else if (JSON.stringify(request.order) !== JSON.stringify(cacheLastRequest.order) ||
				JSON.stringify(request.columns) !== JSON.stringify(cacheLastRequest.columns) ||
				JSON.stringify(request.search) !== JSON.stringify(cacheLastRequest.search)) {
				// Ordering, columns, or search has changed, fetch new data
				ajax = true;
			}

			// Store the current request
			//cacheLastRequest = { ...request };
			cacheLastRequest = JSON.parse(JSON.stringify(request));

			if (ajax) {
				// Need data from the server
				if (requestStart < cacheLower) {
					requestStart = requestStart - requestLength * (conf.pages - 1);

					if (requestStart < 0) {
						requestStart = 0;
					}
				}

				cacheLower = requestStart;
				cacheUpper = requestStart + requestLength * conf.pages;

				request.start = requestStart;
				request.length = requestLength * conf.pages;

				// Add additional data if needed
				if (typeof conf.data === 'function') {
					const additionalData = conf.data(request);
					if (additionalData) {
						Object.assign(request, additionalData);
					}
				} else if (conf.data) {
					Object.assign(request, conf.data);
				}

				// Use `fetch` to make Ajax request
				let response = await fetch(
					conf.url,
					{
						method: conf.method,
						headers: {
							'Content-Type': 'application/json',
							'X-WP-Nonce': request._wpnonce // Add nonce for security
						},
						body: JSON.stringify(request)
					}
				);

				let json = await response.json();

				cacheLastJson = JSON.parse(JSON.stringify(json));

				if (cacheLower !== drawStart) {
					json.data.splice(0, drawStart - cacheLower);
				}
				if (requestLength >= -1) {
					json.data.splice(requestLength, json.data.length);
				}

				drawCallback(json);

			} else {
				let json = JSON.parse(JSON.stringify(cacheLastJson));
				json.draw = request.draw; // Update the echo for each response
				json.data.splice(0, requestStart - cacheLower);
				json.data.splice(requestLength, json.data.length);

				drawCallback(json);
			}
		};
	}
};

// Register an API method that will empty the pipelined data, forcing an Ajax
// fetch on the next draw (i.e. `table.clearPipeline().draw()`)
DataTable.Api.register('clearPipeline()', function () {
	return this.iterator('table', function (settings) {
		settings.clearCache = true;
	});
});


// DOMContentLoaded to trigger the initialization
document.addEventListener('DOMContentLoaded', () => {
	const tableElement = document.querySelector('#tinCanReportTable'); // Select the table element
	if (tableElement) {
		TinCanReportTable.init(tableElement);
	}
});
